import React, { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Clock,
  FilterX,
  Loader2,
  PhoneCall,
  RefreshCw,
  Search,
  ShieldCheck,
  UserCheck,
  Users,
  Zap,
} from 'lucide-react';
import { ApiService } from '../services/api';
import { FleetLead, FleetLeadEvent, FleetLeadStatus } from '../types';
import { useToast } from '../context/ToastContext';

type OwnerFilter = 'all' | 'joshua' | 'tobi' | 'unassigned';
type SlaFilter = 'all' | 'pending' | 'missed' | 'met';

const statusLabels: Record<FleetLeadStatus, string> = {
  new: 'New',
  active_trial: 'Active trial',
  qualified: 'Qualified',
  extension_requested: 'Extension requested',
  converted: 'Converted',
  nurture: 'Nurture',
  closed_lost: 'Closed lost',
};

const highIntentEvents = new Set([
  'trial_started',
  'first_pcn_uploaded',
  'trial_report_generated',
  'pricing_viewed',
  'plan_selected',
  'upgrade_clicked',
  'payment_started',
  'soft_limit_reached',
  'trial_extension_requested',
  'trial_completed',
  'trial_expired',
]);

const noPcnReminderEvents = new Set([
  'no_pcn_uploaded_30m',
  'no_pcn_uploaded_24h',
]);

const buyingIntentEvents = new Set([
  'pricing_viewed',
  'plan_selected',
  'upgrade_clicked',
  'payment_started',
]);

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const minutesUntil = (value?: string | null) => {
  if (!value) return null;
  const due = new Date(value).getTime();
  if (!Number.isFinite(due)) return null;
  return Math.round((due - Date.now()) / 60000);
};

const eventLabel = (eventType: string) =>
  eventType
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const slaTone = (lead: FleetLead) => {
  if (lead.sla_status === 'met') return 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-950/30 dark:border-emerald-900';
  if (lead.sla_status === 'missed') return 'text-red-700 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-950/30 dark:border-red-900';
  const remaining = minutesUntil(lead.first_response_due_at);
  if (remaining !== null && remaining <= 0) return 'text-red-700 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-950/30 dark:border-red-900';
  if (remaining !== null && remaining <= 2) return 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-950/30 dark:border-amber-900';
  return 'text-slate-700 bg-slate-50 border-slate-200 dark:text-slate-300 dark:bg-slate-900 dark:border-slate-800';
};

const statusTone = (status: FleetLeadStatus) => {
  if (status === 'converted') return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900';
  if (status === 'closed_lost') return 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:border-slate-800';
  if (status === 'extension_requested') return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900';
  if (status === 'qualified') return 'bg-primary-50 text-primary-700 border-primary-200 dark:bg-primary-950/30 dark:text-primary-300 dark:border-primary-900';
  return 'bg-white text-slate-700 border-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:border-slate-800';
};

const ownerLabel = (owner?: string | null) => {
  if (!owner) return 'Unassigned';
  return owner.charAt(0).toUpperCase() + owner.slice(1);
};

const InternalLeads: React.FC = () => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isInternalStaff, setIsInternalStaff] = useState(false);
  const [leads, setLeads] = useState<FleetLead[]>([]);
  const [events, setEvents] = useState<FleetLeadEvent[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [owner, setOwner] = useState<OwnerFilter>('all');
  const [status, setStatus] = useState<FleetLeadStatus | 'all'>('all');
  const [sla, setSla] = useState<SlaFilter>('all');
  const [softLimitOnly, setSoftLimitOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');

  const load = async (silent = false) => {
    try {
      if (silent) setRefreshing(true);
      else setLoading(true);
      const data = await ApiService.getFleetLeadDashboard();
      setIsInternalStaff(data.isInternalStaff);
      setLeads(data.leads);
      setEvents(data.events);
      setSelectedId((current) => current || data.leads[0]?.id || null);
    } catch (error: any) {
      addToast(error?.message || 'Unable to load FleetMode lead dashboard.', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const eventsByOrg = useMemo(() => {
    const map = new Map<string, FleetLeadEvent[]>();
    for (const event of events) {
      const list = map.get(event.organisation_id) || [];
      list.push(event);
      map.set(event.organisation_id, list);
    }
    return map;
  }, [events]);

  const decoratedLeads = useMemo(() => {
    return leads.map((lead) => {
      const orgEvents = eventsByOrg.get(lead.organisation_id) || [];
      const hasSoftLimit = orgEvents.some((event) => event.event_type === 'soft_limit_reached');
      const hasHighIntent = orgEvents.some((event) => highIntentEvents.has(event.event_type));
      const hasNoPcnReminder = orgEvents.some((event) => noPcnReminderEvents.has(event.event_type));
      const hasBuyingIntent = orgEvents.some((event) => buyingIntentEvents.has(event.event_type));
      return { lead, events: orgEvents, hasSoftLimit, hasHighIntent, hasNoPcnReminder, hasBuyingIntent };
    });
  }, [eventsByOrg, leads]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return decoratedLeads.filter(({ lead, hasSoftLimit }) => {
      if (q) {
        const haystack = [
          lead.company_name,
          lead.contact_name,
          lead.email,
          lead.phone,
          lead.source,
          lead.next_action,
        ].join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (owner === 'unassigned' && lead.lead_owner) return false;
      if (owner !== 'all' && owner !== 'unassigned' && String(lead.lead_owner || '').toLowerCase() !== owner) return false;
      if (status !== 'all' && lead.status !== status) return false;
      if (sla !== 'all' && lead.sla_status !== sla) return false;
      if (softLimitOnly && !hasSoftLimit) return false;
      return true;
    });
  }, [decoratedLeads, owner, query, sla, softLimitOnly, status]);

  const selected = decoratedLeads.find(({ lead }) => lead.id === selectedId) || filtered[0] || null;

  useEffect(() => {
    setNoteDraft(selected?.lead.notes || '');
  }, [selected?.lead.id]);

  const stats = useMemo(() => {
    const total = decoratedLeads.length;
    const pendingSla = decoratedLeads.filter(({ lead }) => lead.sla_status === 'pending').length;
    const missedSla = decoratedLeads.filter(({ lead }) => lead.sla_status === 'missed').length;
    const highIntent = decoratedLeads.filter(({ hasHighIntent }) => hasHighIntent).length;
    const softLimit = decoratedLeads.filter(({ hasSoftLimit }) => hasSoftLimit).length;
    const noPcnReminders = decoratedLeads.filter(({ hasNoPcnReminder }) => hasNoPcnReminder).length;
    const buyingIntent = decoratedLeads.filter(({ hasBuyingIntent }) => hasBuyingIntent).length;
    const unassigned = decoratedLeads.filter(({ lead }) => !lead.lead_owner).length;
    const activeTrials = decoratedLeads.filter(({ lead }) => lead.status === 'active_trial').length;
    const openPipeline = decoratedLeads.filter(({ lead }) => lead.status !== 'converted' && lead.status !== 'closed_lost').length;
    return { total, pendingSla, missedSla, highIntent, softLimit, noPcnReminders, buyingIntent, unassigned, activeTrials, openPipeline };
  }, [decoratedLeads]);

  const ownerWorkload = useMemo(() => {
    const countFor = (ownerName: OwnerFilter) => decoratedLeads.filter(({ lead }) => String(lead.lead_owner || '').toLowerCase() === ownerName).length;
    return {
      joshua: countFor('joshua'),
      tobi: countFor('tobi'),
      unassigned: decoratedLeads.filter(({ lead }) => !lead.lead_owner).length,
    };
  }, [decoratedLeads]);

  const hasActiveFilters = Boolean(query.trim()) || owner !== 'all' || status !== 'all' || sla !== 'all' || softLimitOnly;

  const resetFilters = () => {
    setQuery('');
    setOwner('all');
    setStatus('all');
    setSla('all');
    setSoftLimitOnly(false);
  };

  const updateLead = async (lead: FleetLead, patch: Partial<FleetLead>) => {
    try {
      setSaving(true);
      const updated = await ApiService.updateFleetLead(lead.id, patch);
      setLeads((current) => current.map((item) => (item.id === lead.id ? { ...item, ...updated } : item)));
      addToast('Lead updated.', 'success');
    } catch (error: any) {
      addToast(error?.message || 'Unable to update lead.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const markContacted = async (lead: FleetLead) => {
    await updateLead(lead, {
      first_response_at: new Date().toISOString(),
      sla_status: 'met',
      next_action: 'Log conversion outcome or schedule follow-up',
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-[480px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!isInternalStaff) {
    return (
      <div className="mx-auto flex min-h-[520px] max-w-2xl flex-col items-center justify-center text-center">
        <div className="rounded-full border border-slate-200 bg-white p-4 text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
          <ShieldCheck size={28} />
        </div>
        <h1 className="mt-5 text-2xl font-bold text-slate-950 dark:text-white">Internal access required</h1>
        <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
          This dashboard is limited to Joshua, Tobi, and approved internal staff. Customer team roles cannot view lead follow-up data.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-950 text-white shadow-sm dark:border-slate-800">
        <div className="relative p-6 lg:p-7">
          <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-primary-500/20 blur-3xl" />
          <div className="absolute bottom-0 right-24 h-28 w-28 rounded-full bg-emerald-400/10 blur-2xl" />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-slate-200">
                <UserCheck size={14} />
                Internal operations
              </div>
              <h1 className="mt-4 text-3xl font-bold tracking-tight lg:text-4xl">FleetMode control dashboard</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">
                Monitor trial motion, SLA risk, buying intent, soft-limit requests, and owner workload from one internal queue.
              </p>
            </div>
            <button
              onClick={() => load(true)}
              disabled={refreshing}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-white/15 bg-white px-4 text-sm font-bold text-slate-950 shadow-sm transition hover:bg-slate-100 disabled:opacity-60"
            >
              {refreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              Refresh
            </button>
          </div>

          <div className="relative mt-6 grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-white/10 bg-white/10 p-4">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-300">
                <Activity size={15} />
                Open pipeline
              </div>
              <div className="mt-2 text-3xl font-bold">{stats.openPipeline}</div>
              <p className="mt-1 text-xs text-slate-300">{stats.total} total leads tracked</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/10 p-4">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-300">
                <Zap size={15} />
                Buying intent
              </div>
              <div className="mt-2 text-3xl font-bold">{stats.buyingIntent}</div>
              <p className="mt-1 text-xs text-slate-300">Pricing, plan, upgrade, or payment signals</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/10 p-4">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-300">
                <AlertTriangle size={15} />
                SLA risk
              </div>
              <div className="mt-2 text-3xl font-bold">{stats.missedSla + stats.pendingSla}</div>
              <p className="mt-1 text-xs text-slate-300">{stats.missedSla} missed, {stats.pendingSla} pending</p>
            </div>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        {[
          {
            label: 'Pending SLA',
            value: stats.pendingSla,
            icon: <Clock size={18} />,
            tone: 'text-amber-700 dark:text-amber-300',
            help: 'Leads still inside the first-response window.'
          },
          {
            label: 'Missed SLA',
            value: stats.missedSla,
            icon: <AlertTriangle size={18} />,
            tone: 'text-red-700 dark:text-red-300',
            help: 'Overdue follow-ups that should be worked first.'
          },
          {
            label: 'High intent',
            value: stats.highIntent,
            icon: <PhoneCall size={18} />,
            tone: 'text-primary-700 dark:text-primary-300',
            help: 'Trial, PCN, report, pricing, upgrade, or limit events.'
          },
          {
            label: 'Buying intent',
            value: stats.buyingIntent,
            icon: <CheckCircle2 size={18} />,
            tone: 'text-emerald-700 dark:text-emerald-300',
            help: 'Pricing, plan selection, upgrade, or checkout signals.'
          },
          {
            label: 'No PCN',
            value: stats.noPcnReminders,
            icon: <AlertTriangle size={18} />,
            tone: 'text-amber-700 dark:text-amber-300',
            help: 'Trial accounts nudged after no first PCN upload.'
          },
          {
            label: 'Soft limits',
            value: stats.softLimit,
            icon: <ShieldCheck size={18} />,
            tone: 'text-slate-700 dark:text-slate-300',
            help: '3-PCN limit or extension review signals.'
          },
        ].map((item) => (
          <div key={item.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className={`flex items-center gap-2 text-sm font-bold ${item.tone}`}>
              {item.icon}
              {item.label}
            </div>
            <div className="mt-3 text-3xl font-bold text-slate-950 dark:text-white">{item.value}</div>
            <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">{item.help}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
            <Users size={18} className="text-primary-600 dark:text-primary-400" />
            Owner workload
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            {[
              ['Joshua', ownerWorkload.joshua],
              ['Tobi', ownerWorkload.tobi],
              ['Unassigned', ownerWorkload.unassigned],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg bg-slate-50 p-3 dark:bg-slate-950/60">
                <div className="text-xl font-bold text-slate-950 dark:text-white">{value}</div>
                <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 lg:col-span-2">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
            <BarChart3 size={18} className="text-primary-600 dark:text-primary-400" />
            Operating rhythm
          </div>
          <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Work missed SLA first, then buying intent, then no-PCN reminders. Keep unassigned leads at zero before the end of each day.
          </p>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="border-b border-slate-200 p-4 dark:border-slate-800">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-950 dark:text-white">Internal queue</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {filtered.length} of {decoratedLeads.length} leads shown
                </p>
              </div>
              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 text-xs font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-800 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FilterX size={14} />
                  Reset filters
                </button>
              )}
            </div>
            <div className="grid min-w-0 gap-3 sm:grid-cols-2 2xl:grid-cols-[minmax(240px,1fr)_minmax(130px,150px)_minmax(150px,170px)_minmax(120px,140px)_minmax(120px,auto)]">
              <div className="relative min-w-0 sm:col-span-2 2xl:col-span-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search company, contact, phone, source"
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                />
              </div>
              <select value={owner} onChange={(event) => setOwner(event.target.value as OwnerFilter)} className="h-10 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white">
                <option value="all">All owners</option>
                <option value="joshua">Joshua</option>
                <option value="tobi">Tobi</option>
                <option value="unassigned">Unassigned</option>
              </select>
              <select value={status} onChange={(event) => setStatus(event.target.value as FleetLeadStatus | 'all')} className="h-10 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white">
                <option value="all">All statuses</option>
                {Object.entries(statusLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <select value={sla} onChange={(event) => setSla(event.target.value as SlaFilter)} className="h-10 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white">
                <option value="all">All SLA</option>
                <option value="pending">Pending</option>
                <option value="missed">Missed</option>
                <option value="met">Met</option>
              </select>
              <label className="inline-flex h-10 w-full min-w-0 items-center gap-2 rounded-lg border border-slate-200 px-3 text-sm font-medium text-slate-700 dark:border-slate-800 dark:text-slate-200">
                <input type="checkbox" checked={softLimitOnly} onChange={(event) => setSoftLimitOnly(event.target.checked)} />
                Soft limit
              </label>
            </div>
          </div>

          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {filtered.length === 0 ? (
              <div className="p-10">
                <div className="mx-auto max-w-md text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    {hasActiveFilters ? <FilterX size={22} /> : <Activity size={22} />}
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
                    {hasActiveFilters ? 'No leads match these filters' : 'No internal leads yet'}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                    {hasActiveFilters
                      ? 'Clear filters to return to the full operations queue, then triage by SLA, buying intent, or owner.'
                      : 'The queue fills from trial starts, first PCNs, pricing intent, report requests, and soft-limit alerts.'}
                  </p>
                  {hasActiveFilters && (
                    <button
                      onClick={resetFilters}
                      className="mt-4 inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-bold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
                    >
                      Reset filters
                      <ArrowRight size={15} />
                    </button>
                  )}
                </div>
              </div>
            ) : filtered.map(({ lead, events: leadEvents, hasSoftLimit, hasHighIntent, hasNoPcnReminder, hasBuyingIntent }) => {
              const remaining = minutesUntil(lead.first_response_due_at);
              return (
                <button
                  key={lead.id}
                  onClick={() => setSelectedId(lead.id)}
                  className={`grid w-full gap-3 p-4 text-left transition hover:bg-slate-50 dark:hover:bg-slate-950/60 lg:grid-cols-[minmax(0,1.2fr)_160px_130px_150px] ${
                    selected?.lead.id === lead.id ? 'bg-primary-50/60 dark:bg-primary-950/20' : ''
                  }`}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate text-sm font-bold text-slate-950 dark:text-white">{lead.company_name}</h2>
                      {hasHighIntent && <span className="rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-bold text-primary-700 dark:bg-primary-950/40 dark:text-primary-300">High intent</span>}
                      {hasBuyingIntent && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">Buying intent</span>}
                      {hasNoPcnReminder && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">No PCN</span>}
                      {hasSoftLimit && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">Soft limit</span>}
                    </div>
                    <div className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                      {lead.contact_name || 'No contact'} {lead.email ? `- ${lead.email}` : ''} {lead.phone ? `- ${lead.phone}` : ''}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Owner</div>
                    <div className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{ownerLabel(lead.lead_owner)}</div>
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Status</div>
                    <div className={`mt-1 inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${statusTone(lead.status)}`}>
                      {statusLabels[lead.status] || lead.status}
                    </div>
                  </div>
                  <div>
                    <div className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${slaTone(lead)}`}>
                      {lead.sla_status === 'pending' && remaining !== null ? `${remaining > 0 ? remaining : 0} min left` : lead.sla_status}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{leadEvents[0] ? eventLabel(leadEvents[0].event_type) : 'No events'}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <aside className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          {!selected ? (
            <div className="p-6">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-primary-50 text-primary-700 dark:bg-primary-950/40 dark:text-primary-300">
                <ShieldCheck size={22} />
              </div>
              <h2 className="mt-4 text-xl font-bold text-slate-950 dark:text-white">Operations playbook</h2>
              <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                Select a lead to update ownership, SLA status, next action, and notes. When the queue is empty, use this panel as the team control checklist.
              </p>
              <div className="mt-5 space-y-3">
                {[
                  'Check missed SLA and call first.',
                  'Assign every unassigned lead to Joshua or Tobi.',
                  'Review pricing and payment intent before no-PCN reminders.',
                  'Log the next action after each contact.',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-700 dark:bg-slate-950/60 dark:text-slate-300">
                    <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0 text-primary-600 dark:text-primary-400" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex h-full flex-col">
              <div className="border-b border-slate-200 p-5 dark:border-slate-800">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Lead detail</div>
                <h2 className="mt-2 text-xl font-bold text-slate-950 dark:text-white">{selected.lead.company_name}</h2>
                <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                  {selected.lead.contact_name || 'No named contact'} {selected.lead.phone ? `- ${selected.lead.phone}` : ''}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-950/60">
                    <div className="font-bold uppercase tracking-wide text-slate-400">Due</div>
                    <div className="mt-1 font-semibold text-slate-800 dark:text-slate-100">{formatDateTime(selected.lead.first_response_due_at)}</div>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-950/60">
                    <div className="font-bold uppercase tracking-wide text-slate-400">Score</div>
                    <div className="mt-1 font-semibold text-slate-800 dark:text-slate-100">{selected.lead.lead_score}</div>
                  </div>
                </div>
              </div>

              <div className="space-y-4 p-5">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    disabled={saving}
                    onClick={() => markContacted(selected.lead)}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-950 px-3 text-sm font-bold text-white transition hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
                  >
                    <CheckCircle2 size={16} />
                    Contacted
                  </button>
                  <button
                    disabled={saving}
                    onClick={() => updateLead(selected.lead, { lead_owner: selected.lead.lead_owner === 'joshua' ? 'tobi' : 'joshua' })}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-800 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <UserCheck size={16} />
                    Assign
                  </button>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-slate-400">Status</label>
                  <select
                    value={selected.lead.status}
                    disabled={saving}
                    onChange={(event) => updateLead(selected.lead, { status: event.target.value as FleetLeadStatus })}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                  >
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-slate-400">Next action</label>
                  <input
                    value={selected.lead.next_action || ''}
                    disabled={saving}
                    onChange={(event) => setLeads((current) => current.map((lead) => (
                      lead.id === selected.lead.id ? { ...lead, next_action: event.target.value } : lead
                    )))}
                    onBlur={(event) => updateLead(selected.lead, { next_action: event.target.value })}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-slate-400">Notes</label>
                  <textarea
                    value={noteDraft}
                    onChange={(event) => setNoteDraft(event.target.value)}
                    rows={4}
                    className="mt-1 w-full resize-none rounded-lg border border-slate-200 bg-white p-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                  />
                  <button
                    disabled={saving}
                    onClick={() => updateLead(selected.lead, { notes: noteDraft })}
                    className="mt-2 h-9 rounded-lg border border-slate-200 px-3 text-xs font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-800 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Save note
                  </button>
                </div>
              </div>

              <div className="border-t border-slate-200 p-5 dark:border-slate-800">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Recent events</div>
                <div className="mt-3 space-y-3">
                  {selected.events.length === 0 ? (
                    <div className="text-sm text-slate-500 dark:text-slate-400">No events logged yet.</div>
                  ) : selected.events.slice(0, 8).map((event) => (
                    <div key={event.id} className="rounded-lg bg-slate-50 p-3 dark:bg-slate-950/60">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-bold text-slate-800 dark:text-slate-100">{eventLabel(event.event_type)}</div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400">{formatDateTime(event.created_at)}</div>
                      </div>
                      {event.metadata_json && Object.keys(event.metadata_json).length > 0 && (
                        <div className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                          {Object.entries(event.metadata_json).slice(0, 3).map(([key, value]) => `${key}: ${String(value)}`).join(' | ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default InternalLeads;
